import { useEffect, useMemo, useState, type ReactNode } from "react";
import { InvoiceMailAttachmentPreview } from "@/components/invoiceMail/InvoiceMailAttachmentPreview";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  approvalStatusClass,
  approvalStatusLabel,
  decideInvoiceApproval,
  eventActionLabel,
  formatDay,
  formatIban,
  formatMoney,
  getInvoiceApproval,
  matchMethodLabel,
  saveInvoiceApprovalHeader,
  type InvoiceApprovalDetail,
  type InvoiceApprovalHeaderInput,
} from "@/lib/invoiceApproval";
import type { InvoiceMailAttachmentSummary } from "@/lib/invoiceMail";
import { toast } from "@/lib/toast";
import { pickInvoiceAttachment } from "@/pages/invoiceApproval/invoicePreview";

interface HeaderForm {
  invoice_number: string;
  invoice_date: string;
  supplier_name: string;
  total_net: string;
  total_gross: string;
  currency: string;
  due_date: string;
  supplier_iban: string;
  payment_terms: string;
}

function emptyForm(): HeaderForm {
  return {
    invoice_number: "",
    invoice_date: "",
    supplier_name: "",
    total_net: "",
    total_gross: "",
    currency: "EUR",
    due_date: "",
    supplier_iban: "",
    payment_terms: "",
  };
}

function formFromDetail(detail: InvoiceApprovalDetail): HeaderForm {
  const header = detail.header;
  return {
    invoice_number: header.invoice_number || "",
    invoice_date: (header.invoice_date || "").slice(0, 10),
    supplier_name: header.supplier_name || "",
    total_net: header.total_net == null ? "" : String(header.total_net),
    total_gross: header.total_gross == null ? "" : String(header.total_gross),
    currency: header.currency || "EUR",
    due_date: (header.due_date || "").slice(0, 10),
    supplier_iban: header.supplier_iban || "",
    payment_terms: header.payment_terms || "",
  };
}

function payloadFromForm(form: HeaderForm): InvoiceApprovalHeaderInput {
  const number = (value: string) => (value.trim() === "" ? null : Number(value));
  return {
    invoice_number: form.invoice_number.trim() || null,
    invoice_date: form.invoice_date || null,
    supplier_name: form.supplier_name.trim() || null,
    total_net: number(form.total_net),
    total_gross: number(form.total_gross),
    currency: form.currency.trim() || "EUR",
    due_date: form.due_date || null,
    supplier_iban: form.supplier_iban.trim() || null,
    payment_terms: form.payment_terms.trim() || null,
  };
}

function duplicateMessage(
  duplicates: { candidate_id: number; supplier_name?: string | null; subject?: string | null }[],
): string {
  const labels = [
    ...new Set(duplicates.map((item) => item.supplier_name || item.subject || `#${item.candidate_id}`)),
  ];
  const who = labels.join(", ");
  if (duplicates.length <= 1) {
    return `Mögliche Dublette: dieselbe Rechnungsnummer liegt bereits vor (${who}).`;
  }
  return `Mögliche Dublette: dieselbe Rechnungsnummer liegt bereits ${duplicates.length}× vor (${who}).`;
}

function Section({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-separator bg-fill-quaternary/20 p-3.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{title}</h3>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs text-label-secondary">{label}</span>
      {children}
    </label>
  );
}

function priceLabel(state: string, percent: number | null | undefined): string {
  if (state === "higher") return `+${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(percent || 0)} % zum EK`;
  if (state === "lower") return `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(percent || 0)} % zum EK`;
  if (state === "match") return "EK stimmt";
  if (state === "no_ek") return "kein EK im Stamm";
  return "kein Stammartikel";
}

export function ApprovalDialog({
  candidateId,
  onClose,
  onChanged,
}: {
  candidateId: number | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<InvoiceApprovalDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<HeaderForm>(emptyForm);
  const [note, setNote] = useState("");
  const [ackDeviation, setAckDeviation] = useState(false);
  const [ackDuplicate, setAckDuplicate] = useState(false);
  const [ackProject, setAckProject] = useState(false);
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (candidateId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNote("");
    setAckDeviation(false);
    setAckDuplicate(false);
    setAckProject(false);
    void getInvoiceApproval(candidateId)
      .then((next) => {
        if (cancelled) return;
        setDetail(next);
        setForm(formFromDetail(next));
        setAttachmentId(pickInvoiceAttachment(next.attachments)?.id || null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Die Rechnung konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [candidateId]);

  const attachment = useMemo<InvoiceMailAttachmentSummary | null>(() => {
    const found = detail?.attachments.find((item) => item.id === attachmentId) || detail?.attachments[0];
    if (!found?.id) return null;
    return { id: found.id, name: found.name, contentType: found.content_type, size: found.size };
  }, [attachmentId, detail]);

  const gross = form.total_gross.trim() === "" ? null : Number(form.total_gross);
  const participants = detail?.approval.participants || [];
  const groupApproval = participants.length >= 2;
  const canRelease =
    Boolean(detail?.permissions.can_decide) &&
    gross != null &&
    !Number.isNaN(gross) &&
    (detail?.price_deviation_count ? ackDeviation : true) &&
    (detail?.duplicates.length ? ackDuplicate : true) &&
    (detail?.missing_project ? ackProject : true);

  function patch<K extends keyof HeaderForm>(key: K, value: HeaderForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function run(
    key: string,
    task: () => Promise<InvoiceApprovalDetail>,
    success: string,
  ) {
    setBusy(key);
    try {
      const next = await task();
      setDetail(next);
      setForm(formFromDetail(next));
      setNote("");
      toast.success(success);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  const currency = form.currency || "EUR";
  const inputClass = "eh-input";
  const previewFrameClass = "absolute inset-0 h-full w-full rounded-none border-0 bg-white";

  return (
    <Dialog
      open={candidateId != null}
      onClose={onClose}
      size="4xl"
      fullScreenMobile
      title={detail?.header.supplier_name || detail?.subject || "Rechnung"}
      description={detail ? approvalStatusLabel(detail.approval.status) : "Rechnungsfreigabe"}
      panelClassName="sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)]"
      bodyClassName="!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !p-0 max-sm:!overflow-hidden"
      footer={
        detail?.permissions.can_decide ? (
          <>
            <Button
              variant="secondary"
              disabled={busy != null || note.trim().length < 3}
              onClick={() =>
                void run(
                  "klaerung",
                  () =>
                    decideInvoiceApproval(detail.candidate_id, {
                      action: "in_klaerung",
                      note,
                      header: payloadFromForm(form),
                    }),
                  "Rechnung ist in Klärung.",
                )
              }
            >
              In Klärung
            </Button>
            <Button
              variant="danger"
              disabled={busy != null || note.trim().length < 3}
              onClick={() =>
                void run(
                  "ablehnen",
                  () =>
                    decideInvoiceApproval(detail.candidate_id, {
                      action: "ablehnen",
                      note,
                      header: payloadFromForm(form),
                    }),
                  "Rechnung abgelehnt.",
                )
              }
            >
              Ablehnen
            </Button>
            <Button
              disabled={busy != null || !canRelease}
              onClick={() =>
                void run(
                  "frei",
                  () =>
                    decideInvoiceApproval(detail.candidate_id, {
                      action: "freigeben",
                      note: note.trim() || null,
                      acknowledge_deviations: ackDeviation,
                      acknowledge_duplicate: ackDuplicate,
                      acknowledge_missing_project: ackProject,
                      header: payloadFromForm(form),
                    }),
                  groupApproval
                    ? participants.filter((person) => person.decision !== "freigegeben").length > 1
                      ? "Ihre Freigabe ist gespeichert. Die Rechnung gilt erst als freigegeben, wenn alle zugestimmt haben."
                      : "Rechnung freigegeben."
                    : "Rechnung freigegeben.",
                )
              }
            >
              {detail.permissions.as_second ? "Endgültig freigeben" : "Freigeben"}
            </Button>
          </>
        ) : null
      }
    >
      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : error ? (
        <div className="p-4">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : detail ? (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="flex h-[min(42dvh,22rem)] min-h-0 shrink-0 flex-col border-b border-separator bg-fill-quaternary/25 lg:h-full lg:w-[58%] lg:border-b-0 lg:border-r">
            <div className="flex shrink-0 items-center gap-2 border-b border-separator bg-card px-3 py-2">
              <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">Beleg</span>
              {detail.attachments.length > 1 ? (
                <select
                  className="eh-input h-8 min-w-0 flex-1 py-1 text-xs"
                  value={attachment?.id || ""}
                  onChange={(event) => setAttachmentId(event.target.value)}
                >
                  {detail.attachments.map((item) => (
                    <option key={item.id || item.name} value={item.id || ""}>
                      {item.name || "Anhang"}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="min-w-0 truncate text-xs text-label-secondary">{attachment?.name || "Kein Anhang"}</span>
              )}
            </div>
            <div className="relative min-h-0 flex-1">
              {attachment ? (
                <InvoiceMailAttachmentPreview
                  candidateId={detail.candidate_id}
                  attachment={attachment}
                  className={previewFrameClass}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-label-secondary">
                  Kein Belegbild vorhanden.
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-separator bg-card/95 px-4 py-2.5 backdrop-blur-sm sm:px-5">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${approvalStatusClass(detail.approval.status)}`}>
                {approvalStatusLabel(detail.approval.status)}
              </span>
              {participants.length > 1 ? (
                <span className="flex flex-wrap gap-1.5">
                  {participants.map((person) => (
                    <span
                      key={person.login}
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        person.decision === "freigegeben"
                          ? "bg-status-green/15 text-status-green"
                          : "bg-fill-tertiary text-label-secondary"
                      }`}
                    >
                      {person.name || person.login}
                      {person.decision === "freigegeben" ? " · freigegeben" : " · offen"}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-sm text-label-secondary">
                  bei {detail.approval.assignee_name || "—"}
                  {detail.approval.second_assignee_name ? ` · 2. ${detail.approval.second_assignee_name}` : ""}
                </span>
              )}
            </div>

            <div className="space-y-4 px-4 py-4 text-sm sm:px-5">
            {detail.permissions.as_substitute ? (
              <Alert variant="info">Sie handeln als Vertretung, die Rechnung ist einer anderen Person zugewiesen.</Alert>
            ) : null}
            {detail.permissions.reason ? <Alert variant="info">{detail.permissions.reason}</Alert> : null}
            {detail.overdue ? <Alert variant="error">Das Fälligkeitsdatum ist überschritten.</Alert> : null}
            {detail.duplicates.length > 0 ? (
              <Alert variant="error">{duplicateMessage(detail.duplicates)}</Alert>
            ) : null}
            {detail.missing_project ? (
              <Alert variant="info">Keine Projekt- oder Bestellzuordnung und noch keine Ablage.</Alert>
            ) : null}

            <Section title="Kopfdaten">
              <Field label="Lieferant">
                <input className={inputClass} value={form.supplier_name} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("supplier_name", event.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Rechnungsnr.">
                  <input className={inputClass} value={form.invoice_number} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("invoice_number", event.target.value)} />
                </Field>
                <Field label="Rechnungsdatum">
                  <input type="date" className={inputClass} value={form.invoice_date} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("invoice_date", event.target.value)} />
                </Field>
                <Field label="Netto">
                  <input type="number" step="0.01" className={inputClass} value={form.total_net} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("total_net", event.target.value)} />
                </Field>
                <Field label="Brutto">
                  <input type="number" step="0.01" className={inputClass} value={form.total_gross} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("total_gross", event.target.value)} />
                </Field>
                <Field label="Währung">
                  <input className={inputClass} value={form.currency} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("currency", event.target.value.toUpperCase())} />
                </Field>
                <Field label="Fällig">
                  <input type="date" className={inputClass} value={form.due_date} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("due_date", event.target.value)} />
                </Field>
              </div>
              <Field label="IBAN">
                <input className={`${inputClass} font-mono`} value={form.supplier_iban} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("supplier_iban", event.target.value)} />
              </Field>
              {form.supplier_iban.trim() ? (
                <p className="-mt-1 font-mono text-xs text-label-tertiary">{formatIban(form.supplier_iban)}</p>
              ) : null}
              <Field label="Zahlungsbedingung">
                <textarea className={inputClass} rows={2} value={form.payment_terms} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("payment_terms", event.target.value)} />
              </Field>
              {detail.header.skonto ? (
                <p className="rounded-lg bg-status-orange/10 px-3 py-2 text-xs font-medium text-status-orange">
                  Skonto {detail.header.skonto.percent} %{detail.header.skonto.until ? ` bis ${formatDay(detail.header.skonto.until)}` : ""}
                </p>
              ) : null}
              {detail.permissions.can_edit_header ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy != null}
                  onClick={() =>
                    void run(
                      "header",
                      () => saveInvoiceApprovalHeader(detail.candidate_id, payloadFromForm(form)),
                      "Kopfdaten gespeichert.",
                    )
                  }
                >
                  Kopfdaten speichern
                </Button>
              ) : null}
            </Section>

            <Section title="Projekte und Ablage">
              {detail.projects.length === 0 && detail.filed_paths.length === 0 ? (
                <p className="text-label-secondary">Noch keinem Projekt zugeordnet.</p>
              ) : (
                <ul className="space-y-2">
                  {detail.projects.map((project) => (
                    <li key={`${project.auftrag_id}-${project.bestellnummer}`} className="rounded-lg bg-card px-3 py-2">
                      <span className="font-medium">{project.auftragsnummer || "Projekt"}</span>
                      {project.jahr ? ` · ${project.jahr}` : ""}
                      {project.beschreibung ? ` · ${project.beschreibung}` : ""}
                      {project.bestellnummer ? (
                        <span className="mt-0.5 block font-mono text-xs text-label-secondary">{project.bestellnummer}</span>
                      ) : null}
                    </li>
                  ))}
                  {detail.filed_paths.map((file) => (
                    <li key={`${file.path}-${file.filename}`} className="text-xs text-label-secondary">
                      {file.web_url ? (
                        <a href={file.web_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                          {file.path || file.filename || "SharePoint"}
                        </a>
                      ) : (
                        file.path || file.filename
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={<>Positionen{detail.line_count > 0 ? ` (${detail.line_count})` : ""}</>}>
              {detail.lines.length === 0 ? (
                <p className="text-label-secondary">Keine E-Rechnung erkannt. Beträge bitte von Hand prüfen.</p>
              ) : (
                <ul className="space-y-2">
                  {detail.lines.map((line) => {
                    const state = line.price.state;
                    const tone =
                      state === "higher"
                        ? "border-status-red/40 bg-status-red/10"
                        : state === "lower"
                          ? "border-status-orange/40 bg-status-orange/10"
                          : state === "match"
                            ? "border-status-green/30 bg-status-green/10"
                            : "border-separator bg-card";
                    return (
                      <li key={line.id} className={`rounded-lg border px-3 py-2 ${tone}`}>
                        <div className="font-medium">{line.bezeichnung}</div>
                        <div className="mt-0.5 text-xs text-label-secondary">
                          {line.menge ?? "—"} {line.einheit || ""} · {formatMoney(line.einzelpreis_netto, currency)}
                          {line.seller_article_nr ? ` · Art. ${line.seller_article_nr}` : ""}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                          {line.stamm_artikel_id ? (
                            <span className="text-label-secondary">
                              Stamm {line.stamm_nr || line.stamm_artikel_id}
                              {line.stamm_name ? ` · ${line.stamm_name}` : ""} · {matchMethodLabel(line.match_method)}
                              {line.price.stamm_ek != null ? ` · EK ${formatMoney(line.price.stamm_ek, currency)}` : ""}
                            </span>
                          ) : (
                            <span className="text-label-secondary">Nicht in der Artikeldatenbank</span>
                          )}
                          <span className="rounded-full bg-card/80 px-2 py-0.5 font-semibold">{priceLabel(state, line.price.percent)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {detail.unmatched_count > 0 ? (
                <p className="text-xs text-label-secondary">
                  {detail.unmatched_count} Position{detail.unmatched_count === 1 ? "" : "en"} ohne Stammartikel.
                </p>
              ) : null}
            </Section>

            {detail.permissions.can_decide ? (
              <Section title="Entscheidung">
                {detail.price_deviation_count > 0 ? (
                  <label className="flex items-start gap-2.5 rounded-lg border border-separator bg-card px-3 py-2 text-sm">
                    <input className="mt-0.5" type="checkbox" checked={ackDeviation} onChange={(event) => setAckDeviation(event.target.checked)} />
                    Preisabweichungen geprüft ({detail.price_deviation_count})
                  </label>
                ) : null}
                {detail.duplicates.length > 0 ? (
                  <label className="flex items-start gap-2.5 rounded-lg border border-separator bg-card px-3 py-2 text-sm">
                    <input className="mt-0.5" type="checkbox" checked={ackDuplicate} onChange={(event) => setAckDuplicate(event.target.checked)} />
                    Dublette geprüft
                  </label>
                ) : null}
                {detail.missing_project ? (
                  <label className="flex items-start gap-2.5 rounded-lg border border-separator bg-card px-3 py-2 text-sm">
                    <input className="mt-0.5" type="checkbox" checked={ackProject} onChange={(event) => setAckProject(event.target.checked)} />
                    Ohne Projektzuordnung freigeben
                  </label>
                ) : null}
                <Field label="Begründung (Pflicht bei Klärung und Ablehnung)">
                  <textarea className={inputClass} rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
                </Field>
                {detail.permissions.can_comment ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy != null || note.trim().length < 3}
                    onClick={() =>
                      void run(
                        "note",
                        () => decideInvoiceApproval(detail.candidate_id, { action: "kommentar", note }),
                        "Kommentar gespeichert.",
                      )
                    }
                  >
                    Nur Kommentar
                  </Button>
                ) : null}
              </Section>
            ) : null}

            <Section title="Protokoll">
              {detail.events.length === 0 ? (
                <p className="text-label-secondary">Noch keine Einträge.</p>
              ) : (
                <ol className="space-y-3 border-l border-separator pl-3">
                  {detail.events.map((event, index) => (
                    <li key={`${event.created_at}-${index}`}>
                      <div className="font-medium text-label">{eventActionLabel(event.action)}</div>
                      <div className="text-xs text-label-secondary">
                        {event.actor_name || event.actor_login || "—"}
                        {event.created_at ? ` · ${formatDay(event.created_at)}` : ""}
                      </div>
                      {event.note ? <p className="mt-0.5 text-xs text-label-secondary">{event.note}</p> : null}
                    </li>
                  ))}
                </ol>
              )}
            </Section>
            </div>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
